var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import shallowEqual from 'shallowequal';
import { supportRef } from 'rc-util/lib/ref';
import { getPathValue, validateValue } from '../utils/valueUtil';
import StickyContext from '../context/StickyContext';
import HoverContext from '../context/HoverContext';
import warning from 'rc-util/lib/warning';
/** Check if cell is in hover range */
function inHoverRange(cellStartRow, cellRowSpan, startRow, endRow) {
    const cellEndRow = cellStartRow + cellRowSpan - 1;
    return cellStartRow <= endRow && cellEndRow >= startRow;
}
function isRenderCell(data) {
    return data && typeof data === 'object' && !Array.isArray(data) && !React.isValidElement(data);
}
function isRefComponent(component) {
    // String tag component also support ref
    if (typeof component === 'string') {
        return true;
    }
    return supportRef(component);
}
function Cell({ prefixCls, className, record, index, renderIndex, dataIndex, render, children, component: Component = 'td', colSpan, rowSpan, // This is already merged on WrapperCell
fixLeft, fixRight, firstFixLeft, lastFixLeft, firstFixRight, lastFixRight, appendNode, additionalProps = {}, ellipsis, align, rowType, isSticky, 
// Hover
hovering, onHover }, ref) {
    var _a, _b;
    const cellPrefixCls = `${prefixCls}-cell`;
    const supportSticky = React.useContext(StickyContext);
    // ==================== Child Node ====================
    let cellProps;
    let childNode;
    if (validateValue(children)) {
        childNode = children;
    }
    else {
        const value = getPathValue(record, dataIndex);
        // Customize render node
        childNode = value;
        if (render) {
            const renderData = render(value, record, renderIndex);
            if (isRenderCell(renderData)) {
                if (process.env.NODE_ENV !== 'production') {
                    warning(false, '`columns.render` return cell props is deprecated with perf issue, please use `onCell` instead.');
                }
                childNode = renderData.children;
                cellProps = renderData.props;
            }
            else {
                childNode = renderData;
            }
        }
    }
    // Not crash if final `childNode` is not validate ReactNode
    if (typeof childNode === 'object' &&
        !Array.isArray(childNode) &&
        !React.isValidElement(childNode)) {
        childNode = null;
    }
    if (ellipsis && (lastFixLeft || firstFixRight)) {
        childNode = <span className={`${cellPrefixCls}-content`}>{childNode}</span>;
    }
    const _c = cellProps || {}, { colSpan: cellColSpan, rowSpan: cellRowSpan, style: cellStyle, className: cellClassName } = _c, restCellProps = __rest(_c, ["colSpan", "rowSpan", "style", "className"]);
    const mergedColSpan = (_a = (cellColSpan !== undefined ? cellColSpan : colSpan)) !== null && _a !== void 0 ? _a : 1;
    const mergedRowSpan = (_b = (cellRowSpan !== undefined ? cellRowSpan : rowSpan)) !== null && _b !== void 0 ? _b : 1;
    if (mergedColSpan === 0 || mergedRowSpan === 0) {
        return null;
    }
    // ====================== Fixed =======================
    const fixedStyle = {};
    const isFixLeft = typeof fixLeft === 'number' && supportSticky;
    const isFixRight = typeof fixRight === 'number' && supportSticky;
    if (isFixLeft) {
        fixedStyle.position = 'sticky';
        fixedStyle.left = fixLeft;
    }
    if (isFixRight) {
        fixedStyle.position = 'sticky';
        fixedStyle.right = fixRight;
    }
    // ====================== Align =======================
    const alignStyle = {};
    if (align) {
        alignStyle.textAlign = align;
    }
    // ====================== Hover =======================
    const onMouseEnter = event => {
        var _a;
        if (record) {
            onHover(index, index + mergedRowSpan - 1);
        }
        (_a = additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.onMouseEnter) === null || _a === void 0 ? void 0 : _a.call(additionalProps, event);
    };
    const onMouseLeave = event => {
        var _a;
        if (record) {
            onHover(-1, -1);
        }
        (_a = additionalProps === null || additionalProps === void 0 ? void 0 : additionalProps.onMouseLeave) === null || _a === void 0 ? void 0 : _a.call(additionalProps, event);
    };
    // ====================== Render ======================
    let title;
    const ellipsisConfig = ellipsis === true ? { showTitle: true } : ellipsis;
    if (ellipsisConfig && (ellipsisConfig.showTitle || rowType === 'header')) {
        if (typeof childNode === 'string' || typeof childNode === 'number') {
            title = childNode.toString();
        }
        else if (React.isValidElement(childNode) && typeof childNode.props.children === 'string') {
            title = childNode.props.children;
        }
    }
    const componentProps = Object.assign(Object.assign(Object.assign({ title }, restCellProps), additionalProps), { colSpan: mergedColSpan !== 1 ? mergedColSpan : null, rowSpan: mergedRowSpan !== 1 ? mergedRowSpan : null, className: classNames(cellPrefixCls, className, {
            [`${cellPrefixCls}-fix-left`]: isFixLeft && supportSticky,
            [`${cellPrefixCls}-fix-left-first`]: firstFixLeft && supportSticky,
            [`${cellPrefixCls}-fix-left-last`]: lastFixLeft && supportSticky,
            [`${cellPrefixCls}-fix-right`]: isFixRight && supportSticky,
            [`${cellPrefixCls}-fix-right-first`]: firstFixRight && supportSticky,
            [`${cellPrefixCls}-fix-right-last`]: lastFixRight && supportSticky,
            [`${cellPrefixCls}-ellipsis`]: ellipsis,
            [`${cellPrefixCls}-with-append`]: appendNode,
            [`${cellPrefixCls}-fix-sticky`]: (isFixLeft || isFixRight) && isSticky && supportSticky,
            [`${cellPrefixCls}-row-hover`]: !cellProps && hovering
        }, additionalProps.className, cellClassName), style: Object.assign(Object.assign(Object.assign(Object.assign({}, additionalProps.style), alignStyle), fixedStyle), cellStyle), onMouseEnter,
        onMouseLeave, ref: isRefComponent(Component) ? ref : null });
    return (<Component {...componentProps}>
            {appendNode
            ? (<div className={`${cellPrefixCls}-with-append-inner`}>
                    {appendNode}
                    {childNode}
                </div>)
            : <>{childNode}</>}
        </Component>);
}
const RefCell = React.forwardRef(Cell);
RefCell.displayName = 'Cell';
// const comparePropList: (keyof InternalCellProps<any>)[] = ['expanded', 'className', 'hovering'];
const comparePropList = ['expanded', 'className'];
const MemoCell = React.memo(RefCell, (prev, next) => {
    if (next.shouldCellUpdate) {
        return (
        // Additional handle of expanded logic
        comparePropList.every(propName => prev[propName] === next[propName]) &&
            // User control update logic
            !next.shouldCellUpdate(next.record, prev.record));
    }
    return shallowEqual(prev, next);
});
/** Inject hover data here, we still wish MemoCell keep simple `shouldCellUpdate` logic */
const WrappedCell = React.forwardRef((props, ref) => {
    const { onHover, startRow, endRow } = React.useContext(HoverContext);
    const { index, additionalProps = {}, colSpan, rowSpan } = props;
    const { colSpan: cellColSpan, rowSpan: cellRowSpan } = additionalProps;
    const mergedColSpan = colSpan !== null && colSpan !== void 0 ? colSpan : cellColSpan;
    const mergedRowSpan = rowSpan !== null && rowSpan !== void 0 ? rowSpan : cellRowSpan;
    const hovering = inHoverRange(index, mergedRowSpan || 1, startRow, endRow);
    return (<MemoCell {...props} colSpan={mergedColSpan} rowSpan={mergedRowSpan} hovering={hovering} ref={ref} onHover={onHover}/>);
});
WrappedCell.displayName = 'WrappedCell';
export default WrappedCell;
